import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      toast.error('Invalid payment session');
      navigate('/pricing');
      return;
    }

    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setLoading(false);
      toast.error('Payment verification timed out. Please contact support.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/payments/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.payment_status === 'paid') {
        setPaymentStatus('success');
        setLoading(false);
        toast.success('Payment successful! Welcome to AIPedia Pro!');
        return;
      } else if (response.data.status === 'expired') {
        setPaymentStatus('expired');
        setLoading(false);
        toast.error('Payment session expired');
        return;
      }

      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      setLoading(false);
      toast.error('Failed to verify payment');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: '#0a0a0a'}}>
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <Loader2 className="w-16 h-16 mx-auto animate-spin" style={{color: '#f59e0b'}} />
            <h3 className="text-xl font-semibold" style={{fontFamily: 'Outfit'}}>Verifying Payment...</h3>
            <p className="text-muted-foreground">Please wait while we confirm your subscription</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{background: '#0a0a0a'}}>
      <Card className="w-full max-w-md" data-testid="payment-success-card">
        <CardHeader>
          <div className="mx-auto mb-4">
            <CheckCircle2 className="w-16 h-16" style={{color: '#10b981'}} />
          </div>
          <CardTitle className="text-center text-3xl" style={{fontFamily: 'Outfit'}}>
            {paymentStatus === 'success' ? 'Welcome to AIPedia Pro!' : 'Payment Status'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-center">
          <p className="text-muted-foreground">
            {paymentStatus === 'success' 
              ? 'Your subscription is now active. Enjoy unlimited access to all premium AI prompts!'
              : 'There was an issue with your payment. Please try again.'}
          </p>
          <Button 
            className="w-full" 
            onClick={() => navigate('/dashboard')}
            data-testid="go-dashboard-btn"
          >
            Go to Dashboard
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
